import ElectricBoltIcon from '@mui/icons-material/ElectricBolt'
import {
    Box,
    Button,
    Drawer,
    Skeleton,
    SvgIcon,
    Typography
} from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'
import PropTypes from 'prop-types'
import { useEffect } from 'react'
import { Link as RouterLink, useLocation } from 'react-router-dom'
import { SPACE_LABELS } from '@/features/docs/docsCategory'
import { categoryForPath } from '@/features/docs/docsNav'
import { useDocsNavContext } from '@/features/docs/docsNavContext'
import ChevronLeftIcon from '@/icons/ChevronLeft'
import { useNavCssVars } from '@/layout/dashboard/use-nav-css-vars'
import { NavSection } from './NavSection'

/**
 * The docs sidebar, scoped to the category being read.
 *
 * It used to list the entire docs site from a hand-maintained literal in
 * this file: roughly four hundred lines restating a `meta.section` every
 * page already declared, in which two "Developer Reference" sections
 * together held thirty items. The two declarations had drifted — pages
 * sat on disk in no section at all, reachable only by typing their URL,
 * and a comment in the old file recorded one such discovery as a fix.
 *
 * Both problems have one fix: derive the tree from the pages
 * (`features/docs/docsNav.js`), and show only the category the reader is
 * in. Moving between categories is the Categories control in the navbar,
 * which is reachable from every page and at every width.
 *
 * Nothing here decides who may read what. The nav is assembled from the
 * manifests `docAccess` already permits, exactly as the old literal was
 * filtered through `canViewDoc`.
 */
const DocsSidebar = (props) => {
    const { onMobileClose, openMobile } = props
    const location = useLocation()
    const lgUp = useMediaQuery((theme) => theme.breakpoints.up('lg'))
    // NavSection/NavItem style themselves entirely off `--nav-item-*`
    // custom properties. Unlike the dashboard sidebar, nothing here
    // defines them, so hover and active states would be no-ops without
    // this.
    const cssVars = useNavCssVars('discreet')
    const { nav, loading } = useDocsNavContext()

    useEffect(() => {
        if (openMobile && onMobileClose) {
            onMobileClose()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname])

    const category = categoryForPath(nav, location.pathname)

    const content = (
        <Box height="100%" display="flex" flexDirection="column">
            <Box sx={{ display: { lg: 'none' }, p: 2 }}>
                <RouterLink to="/">
                    <ElectricBoltIcon
                        sx={(theme) => ({
                            fontSize: 40,
                            // The selected accent colour resolves to the
                            // theme's primary palette; fall back to inherit.
                            color: theme.palette?.primary?.main ?? 'inherit'
                        })}
                    />
                </RouterLink>
            </Box>

            <Box p={2}>
                {loading && (
                    <Box sx={{ px: 1 }}>
                        <Skeleton width="60%" height={18} />
                        <Skeleton width="85%" height={28} />
                        <Skeleton width="75%" height={28} />
                        <Skeleton width="80%" height={28} />
                    </Box>
                )}

                {!loading && category && (
                    <Box
                        data-testid="docs-sidebar-category"
                        data-category-slug={category.slug}
                    >
                        <Button
                            component={RouterLink}
                            to={`/docs/category/${category.slug}`}
                            size="small"
                            startIcon={
                                <SvgIcon fontSize="small">
                                    <ChevronLeftIcon />
                                </SvgIcon>
                            }
                            sx={{
                                color: 'text.secondary',
                                justifyContent: 'flex-start',
                                mb: 1,
                                textTransform: 'none'
                            }}
                        >
                            All of {category.text}
                        </Button>

                        <Typography
                            color="text.secondary"
                            variant="caption"
                            sx={{ display: 'block', ml: 1, mb: 1 }}
                        >
                            {SPACE_LABELS[category.space]}
                        </Typography>

                        <NavSection
                            pathname={location.pathname}
                            subheader={category.label}
                            items={category.pages.map((page) => ({
                                title: page.title,
                                path: page.path,
                                visible: true
                            }))}
                            sx={{ listStyle: 'none', pl: 2 }}
                        />
                    </Box>
                )}

                {/*
                 * A docs page in no visible category: an unknown path, the
                 * landing page itself, or a category index. Offer the way
                 * back rather than an empty rail.
                 */}
                {!loading && !category && (
                    <Button
                        component={RouterLink}
                        to="/docs"
                        size="small"
                        sx={{ textTransform: 'none' }}
                    >
                        Browse all documentation
                    </Button>
                )}
            </Box>
        </Box>
    )

    if (lgUp) {
        return (
            <Drawer
                anchor="left"
                open
                variant="permanent"
                slotProps={{
                    paper: {
                        sx: {
                            ...cssVars,
                            backgroundColor: 'background.paper',
                            height: 'calc(100% - 64px) !important',
                            top: '64px !important',
                            width: 256
                        }
                    }
                }}
            >
                {content}
            </Drawer>
        )
    }

    return (
        <Drawer
            anchor="left"
            onClose={onMobileClose}
            open={openMobile}
            variant="temporary"
            slotProps={{
                paper: {
                    sx: {
                        ...cssVars,
                        backgroundColor: 'background.default',
                        width: 256
                    }
                }
            }}
        >
            {content}
        </Drawer>
    )
}

DocsSidebar.propTypes = {
    onMobileClose: PropTypes.func,
    openMobile: PropTypes.bool
}

export default DocsSidebar
